import { Alert, Box, BoxProps, Text, VStack } from "@chakra-ui/react";
import { ReactNode, useEffect } from "react";
import { GENERIC_ERROR } from "src/constants";
import { isNetworkFetchError } from "src/services/error";

type Props = {
  message?: ReactNode;
  error?: unknown;
  variant?: "alert" | "inline";
} & BoxProps;

const GENERIC_ERROR_TITLE = GENERIC_ERROR.title;

function ErrorMessageContent({ detail }: { detail: ReactNode }) {
  return (
    <VStack textAlign="center" gap={1} width="100%">
      <Text fontWeight="600">{GENERIC_ERROR_TITLE}</Text>
      <Text>{detail}</Text>
    </VStack>
  );
}

function getNetworkErrorDetail(): string {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return "Check your internet connection and try again.";
  }

  return "Check your internet connection and try again. If the problem continues, contact our support team.";
}

function getErrorMessage(error: Error): ReactNode {
  const detail = isNetworkFetchError(error)
    ? getNetworkErrorDetail()
    : error.message;

  return <ErrorMessageContent detail={detail} />;
}

export const GenericError: React.FC<Props> = ({
  message,
  error,
  variant = "alert",
  ...boxProps
}) => {
  useEffect(() => {
    console.error(error);
  }, [error]);

  const errorMessage =
    message ??
    (error instanceof Error ? getErrorMessage(error) : GENERIC_ERROR_TITLE);

  switch (variant) {
    case "inline":
      return (
        <Box as="span" color="red.500" padding={1} {...boxProps}>
          {errorMessage}
        </Box>
      );
    case "alert":
      return (
        <Alert status="error" {...boxProps}>
          {errorMessage}
        </Alert>
      ); // TODO better default generic error
  }
};
